import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import toast from 'react-hot-toast'
import {
  FaExchangeAlt,
  FaFileExcel,
  FaPause,
  FaPlay,
  FaPlus,
  FaSearch,
  FaUpload
} from 'react-icons/fa'
import { AutoComplete } from 'primereact/autocomplete'
import { fetchData, downloadFile } from '../../helpers/fetchData'
import ReusableModal from '../../shared/ReuseableModal'
import DatePicker from '../../shared/DatePicker'
import CustomButton from '../../shared/CustomButton'
import CustomTabs from '../../shared/CustomTabs'
import UploadExcelFile from '../../shared/UploadExcelFile'
import CustomTable from '../../shared/CustomTable'
import TextInput from '../../shared/TextInput'
import { DeleteIcon } from '../../utils/TableActionIcons'

const getStatusColors = status => {
  switch ((status || '').toLowerCase()) {
    case 'completed':
      return {
        pill: 'bg-green-500 text-white',
        bar: 'bg-green-500'
      }
    case 'failed':
      return {
        pill: 'bg-red-500 text-white',
        bar: 'bg-red-500'
      }
    case 'stopped':
      return {
        pill: 'bg-gray-500 text-white',
        bar: 'bg-gray-500'
      }
    case 'stop_requested':
      return {
        pill: 'bg-orange-500 text-white',
        bar: 'bg-orange-400'
      }
    case 'running':
    case 'processing':
      return {
        pill: 'bg-amber-500 text-white',
        bar: 'bg-blue'
      }
    default:
      return {
        pill: 'bg-gray-500 text-white',
        bar: 'bg-blue'
      }
  }
}

const initialDateState = () => ({
  start_date: moment().subtract(1, 'month').format('YYYY-MM-DD'),
  end_date: moment().format('YYYY-MM-DD')
})

const emptyAuditSummary = {
  total_codes: 0,
  inactive_codes: 0,
  not_found_codes: 0,
  exported_rows: 0
}

const emptyUploadStats = {
  total_rows: 0,
  updated_rows: 0,
  skipped_rows: 0,
  updated_docs: 0
}

const isAuditTerminalStatus = status =>
  ['completed', 'failed', 'stopped'].includes((status || '').toLowerCase())

const canResumeAuditStatus = status =>
  ['stopped', 'stop_requested'].includes((status || '').toLowerCase())

const isUploadTerminalStatus = status =>
  ['completed', 'failed', 'stopped'].includes((status || '').toLowerCase())

const canResumeUploadStatus = status =>
  ['stopped', 'stop_requested'].includes((status || '').toLowerCase())

const ICDBulkReplaceModal = ({
  isVisible,
  onClose,
  onSuccess,
  defaultTab = 0
}) => {
  const { t, i18n } = useTranslation()
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [loading, setLoading] = useState(false)
  const [statusLoading, setStatusLoading] = useState(false)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [uploadStatusLoading, setUploadStatusLoading] = useState(false)
  const [stopResumeLoading, setStopResumeLoading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [jobId, setJobId] = useState('')
  const [jobStatus, setJobStatus] = useState(null)
  const [uploadJobId, setUploadJobId] = useState('')
  const [uploadJobStatus, setUploadJobStatus] = useState(null)
  const [file, setFile] = useState(null)
  const [hasAutoDownloaded, setHasAutoDownloaded] = useState(false)
  const [auditCompletionHandled, setAuditCompletionHandled] = useState(false)
  const [uploadCompletionHandled, setUploadCompletionHandled] = useState(false)
  const [manualReplaceLoading, setManualReplaceLoading] = useState(false)
  const [icdSuggestions, setIcdSuggestions] = useState([])
  const [searchingIcd, setSearchingIcd] = useState(false)
  const [shouldPollAuditStatus, setShouldPollAuditStatus] = useState(false)
  const [shouldPollUploadStatus, setShouldPollUploadStatus] = useState(false)
  const [replacements, setReplacements] = useState([
    { old_code: '', new_code: '' }
  ])
  const [formData, setFormData] = useState(initialDateState)

  useEffect(() => {
    if (isVisible) {
      setActiveTabIndex(0)
    }
  }, [isVisible])

  const auditStatus = (jobStatus?.status || '').toLowerCase()
  const uploadStatus = (uploadJobStatus?.status || '').toLowerCase()

  const progressValue = useMemo(() => {
    if (auditStatus === 'completed') return 100
    return 0
  }, [auditStatus])

  const uploadProgressValue = useMemo(() => {
    if (uploadStatus === 'completed') return 100
    return 0
  }, [uploadStatus])

  const summaryStats = useMemo(() => {
    const summary = jobStatus?.summary || {}
    return {
      totalCodes: Number(summary.total_codes || 0),
      inactiveCodes: Number(summary.inactive_codes || 0),
      notFoundCodes: Number(summary.not_found_codes || 0),
      exportedRows: Number(summary.exported_rows || 0)
    }
  }, [jobStatus])

  const uploadStats = useMemo(() => {
    const stats = uploadJobStatus?.stats || {}
    return {
      totalRows: Number(stats.total_rows || 0),
      updatedRows: Number(stats.updated_rows || 0),
      skippedRows: Number(stats.skipped_rows || 0),
      updatedDocs: Number(stats.updated_docs || 0)
    }
  }, [uploadJobStatus])

  const summaryCards = useMemo(
    () => [
      {
        key: 'totalCodes',
        label: t('Total Invalid Codes'),
        value: summaryStats.totalCodes,
        className: 'bg-sky-50 text-sky-500'
      },
      {
        key: 'inactiveCodes',
        label: t('Inactive Codes'),
        value: summaryStats.inactiveCodes,
        className: 'bg-amber-50 text-amber-600'
      },
      {
        key: 'notFoundCodes',
        label: t('Not Found Codes'),
        value: summaryStats.notFoundCodes,
        className: 'bg-red-50 text-red-500'
      },
      {
        key: 'exportedRows',
        label: t('Excel Rows'),
        value: summaryStats.exportedRows,
        className: 'bg-green-50 text-green-600'
      }
    ],
    [summaryStats, t]
  )

  const uploadCards = useMemo(
    () => [
      {
        key: 'totalRows',
        label: t('Total Rows'),
        value: uploadStats.totalRows,
        className: 'bg-sky-50 text-sky-500'
      },
      {
        key: 'updatedRows',
        label: t('Updated Rows'),
        value: uploadStats.updatedRows,
        className: 'bg-green-50 text-green-600'
      },
      {
        key: 'skippedRows',
        label: t('Skipped Rows'),
        value: uploadStats.skippedRows,
        className: 'bg-amber-50 text-amber-600'
      },
      {
        key: 'updatedDocs',
        label: t('Updated EMRs'),
        value: uploadStats.updatedDocs,
        className: 'bg-blue-50 text-blue-600'
      }
    ],
    [uploadStats, t]
  )

  const statusColors = useMemo(
    () => getStatusColors(jobStatus?.status),
    [jobStatus?.status]
  )

  const uploadStatusColors = useMemo(
    () => getStatusColors(uploadJobStatus?.status),
    [uploadJobStatus?.status]
  )

  const handleDateChange = event => {
    const { name, value } = event.target || {}
    setFormData(prev => ({
      ...prev,
      [name]: moment(value).format('YYYY-MM-DD')
    }))
  }

  const addReplacement = () => {
    if (replacements.length >= 500) {
      toast.error(t('Maximum 500 replacements per request'))
      return
    }
    setReplacements(prev => [...prev, { old_code: '', new_code: '' }])
  }

  const removeReplacement = index => {
    if (replacements.length === 1) {
      toast.error(t('At least one replacement is required'))
      return
    }
    setReplacements(prev => prev.filter((_, i) => i !== index))
  }

  const updateReplacement = (index, field, value) => {
    setReplacements(prev => {
      const updated = [...prev]
      updated[index][field] = value
      return updated
    })
  }

  const formatIcdOptions = data =>
    data?.map(icd => ({
      label: `${icd.code_id} - ${icd.ascii_description}`,
      value: icd.code_id,
      data: icd
    })) || []

  const handleSearchIcd = async event => {
    if (!event?.query || event.query.length < 3) {
      setIcdSuggestions([])
      return
    }
    setSearchingIcd(true)
    try {
      const response = await fetchData(
        'GET',
        `/v3/icd/search?search=${event.query}`
      )
      setIcdSuggestions(formatIcdOptions(response?.data || []))
    } catch (error) {
      setIcdSuggestions([])
    } finally {
      setSearchingIcd(false)
    }
  }

  const handleSelectIcd = (event, rowIndex) => {
    if (event?.value?.data) {
      updateReplacement(rowIndex, 'new_code', event.value.data.code_id)
    }
  }

  const fetchAuditJobStatus = async currentJobId => {
    if (!currentJobId) return null

    setStatusLoading(true)
    try {
      const response = await fetchData(
        'GET',
        `/v3/invoices/audit/icd-audit/job/${currentJobId}`
      )
      if (response) {
        setJobStatus(response)
        return response
      }
      return null
    } finally {
      setStatusLoading(false)
    }
  }

  const fetchUploadJobStatus = async currentJobId => {
    if (!currentJobId) return null

    setUploadStatusLoading(true)
    try {
      const response = await fetchData(
        'GET',
        `/v3/migration/icd-invalid/upload-excel/job/${currentJobId}`
      )
      if (response) {
        setUploadJobStatus(response)
        return response
      }
      return null
    } finally {
      setUploadStatusLoading(false)
    }
  }

  useEffect(() => {
    if (!isVisible || !jobId || !shouldPollAuditStatus) return undefined

    let intervalId = null
    const poll = async () => {
      const response = await fetchAuditJobStatus(jobId)
      const status = response?.status?.toLowerCase()
      if (isAuditTerminalStatus(status)) {
        setShouldPollAuditStatus(false)
        if (intervalId) clearInterval(intervalId)
      }
    }

    poll()
    intervalId = setInterval(poll, 4000)

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [isVisible, jobId, shouldPollAuditStatus])

  useEffect(() => {
    if (!isVisible || !uploadJobId || !shouldPollUploadStatus) return undefined

    let intervalId = null
    const poll = async () => {
      const response = await fetchUploadJobStatus(uploadJobId)
      const status = response?.status?.toLowerCase()
      if (isUploadTerminalStatus(status)) {
        setShouldPollUploadStatus(false)
        if (intervalId) clearInterval(intervalId)
      }
    }

    poll()
    intervalId = setInterval(poll, 4000)

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [isVisible, uploadJobId, shouldPollUploadStatus])

  useEffect(() => {
    if (
      !jobStatus ||
      auditCompletionHandled ||
      !['completed', 'failed'].includes(auditStatus)
    ) {
      return
    }

    setAuditCompletionHandled(true)

    if (auditStatus === 'failed') {
      toast.error(jobStatus?.error || t('ICD audit failed'))
      return
    }

    onSuccess?.(jobStatus)

    if (jobStatus?.download_url) {
      const fileName = jobStatus?.file_name || 'icd_audit.xlsx'
      setDownloading(true)
      downloadFile(
        'GET',
        jobStatus.download_url.replace('/api', ''),
        {},
        fileName
      )
        .then(() => {
          setHasAutoDownloaded(true)
          toast.success(
            t('ICD audit completed and Excel downloaded successfully')
          )
        })
        .finally(() => {
          setDownloading(false)
        })
      return
    }

    toast.success(t('ICD audit completed successfully'))
  }, [auditCompletionHandled, auditStatus, jobStatus, onSuccess, t])

  useEffect(() => {
    if (
      !uploadJobStatus ||
      uploadCompletionHandled ||
      (uploadStatus !== 'completed' && uploadStatus !== 'failed')
    ) {
      return
    }

    setUploadCompletionHandled(true)

    if (uploadStatus === 'failed') {
      toast.error(uploadJobStatus?.error || t('ICD upload job failed'))
      return
    }

    toast.success(t('ICD upload job completed successfully'))
    onSuccess?.(uploadJobStatus)
    setFile(null)
  }, [onSuccess, t, uploadCompletionHandled, uploadJobStatus, uploadStatus])

  const handleSubmitAudit = async () => {
    if (!formData.start_date || !formData.end_date) {
      toast.error(t('Please select both start and end dates'))
      return
    }

    if (moment(formData.start_date).isAfter(moment(formData.end_date))) {
      toast.error(t('Start date must be before end date'))
      return
    }

    setLoading(true)
    const payload = {
      audit_type: 'audit_by_date_range',
      start_date: formData.start_date,
      end_date: formData.end_date
    }

    const response = await fetchData(
      'POST',
      '/v3/invoices/audit/icd-audit/bulk',
      payload
    )
    setLoading(false)

    if (response?.job_id) {
      setJobId(response.job_id)
      setShouldPollAuditStatus(true)
      setHasAutoDownloaded(false)
      setAuditCompletionHandled(false)
      setJobStatus({
        job_id: response.job_id,
        status: response.status || 'queued',
        created_at: response.created_at,
        audit_type: 'audit_by_date_range',
        start_date: payload.start_date,
        end_date: payload.end_date,
        download_url: `/v3/invoices/audit/icd-audit/job/${response.job_id}/download`,
        summary: emptyAuditSummary
      })
      toast.success(response.message || t('ICD audit started successfully'))
    }
  }

  const handleStopAuditJob = async () => {
    if (!jobId) return

    setStopResumeLoading(true)
    try {
      const response = await fetchData(
        'POST',
        `/v3/invoices/audit/icd-audit/job/${jobId}/stop`,
        {}
      )
      if (response) {
        const nextStatus = response.status || 'stopped'
        setJobStatus(prev => ({
          ...(prev || {}),
          ...response,
          job_id: jobId,
          status: nextStatus
        }))
        setShouldPollAuditStatus(false)
        toast.success(response.message || t('ICD audit stop requested'))
      }
    } finally {
      setStopResumeLoading(false)
    }
  }

  const handleResumeAuditJob = async () => {
    if (!jobId) return

    setStopResumeLoading(true)
    try {
      const response = await fetchData(
        'POST',
        `/v3/invoices/audit/icd-audit/job/${jobId}/resume`,
        {}
      )
      if (response) {
        setAuditCompletionHandled(false)
        setHasAutoDownloaded(false)
        setShouldPollAuditStatus(true)
        setJobStatus(prev => ({
          ...(prev || {}),
          ...response,
          job_id: jobId,
          status: response.status || 'queued',
          start_date: prev?.start_date || formData.start_date,
          end_date: prev?.end_date || formData.end_date,
          summary: emptyAuditSummary,
          download_url: `/v3/invoices/audit/icd-audit/job/${jobId}/download`
        }))
        toast.success(response.message || t('ICD audit resumed successfully'))
      }
    } finally {
      setStopResumeLoading(false)
    }
  }

  const handleUpload = async () => {
    if (!file) {
      toast.error(t('please_select_file_to_upload'))
      return
    }

    if (!formData.start_date || !formData.end_date) {
      toast.error(t('Please select both start and end dates'))
      return
    }

    if (moment(formData.start_date).isAfter(moment(formData.end_date))) {
      toast.error(t('Start date must be before end date'))
      return
    }

    const uploadFormData = new FormData()
    uploadFormData.append('file', file)
    uploadFormData.append('from_date', formData.start_date)
    uploadFormData.append('to_date', formData.end_date)

    setUploadLoading(true)
    const response = await fetchData(
      'POST',
      '/v3/migration/icd-invalid/upload-excel',
      uploadFormData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
    setUploadLoading(false)

    if (response?.job_id) {
      setUploadJobId(response.job_id)
      setShouldPollUploadStatus(true)
      setUploadCompletionHandled(false)
      setUploadJobStatus({
        job_id: response.job_id,
        status: response.status || 'queued',
        created_at: response.created_at,
        from_date: response.from_date,
        to_date: response.to_date,
        stats: emptyUploadStats
      })
      toast.success(response.message || t('ICD upload job started successfully'))
    }
  }

  const handleStopUploadJob = async () => {
    if (!uploadJobId) return

    setStopResumeLoading(true)
    try {
      const response = await fetchData(
        'POST',
        `/v3/migration/icd-invalid/upload-excel/job/${uploadJobId}/stop`,
        {}
      )
      if (response) {
        setUploadJobStatus(prev => ({
          ...(prev || {}),
          ...response,
          job_id: uploadJobId,
          status: response.status || 'stopped'
        }))
        setShouldPollUploadStatus(false)
        toast.success(response.message || t('ICD upload stop requested'))
      }
    } finally {
      setStopResumeLoading(false)
    }
  }

  const handleResumeUploadJob = async () => {
    if (!uploadJobId) return

    setStopResumeLoading(true)
    try {
      const response = await fetchData(
        'POST',
        `/v3/migration/icd-invalid/upload-excel/job/${uploadJobId}/resume`,
        {}
      )
      if (response) {
        setUploadCompletionHandled(false)
        setShouldPollUploadStatus(true)
        setUploadJobStatus(prev => ({
          ...(prev || {}),
          ...response,
          job_id: uploadJobId,
          status: response.status || 'queued',
          from_date: prev?.from_date || formData.start_date,
          to_date: prev?.to_date || formData.end_date,
          stats: emptyUploadStats
        }))
        toast.success(response.message || t('ICD upload job resumed successfully'))
      }
    } finally {
      setStopResumeLoading(false)
    }
  }

  const handleManualReplace = async () => {
    if (!formData.start_date || !formData.end_date) {
      toast.error(t('Start date and end date are required'))
      return
    }

    const emptyPairs = replacements.filter(r => !r.old_code || !r.new_code)
    if (emptyPairs.length > 0) {
      toast.error(t('All replacement pairs must have both old and new codes'))
      return
    }

    if (moment(formData.end_date).isBefore(moment(formData.start_date))) {
      toast.error(t('End date must be on or after start date'))
      return
    }

    const payload = {
      replacements: replacements.map(r => ({
        old_code: r.old_code.trim(),
        new_code: r.new_code.trim()
      })),
      start_date: formData.start_date,
      end_date: formData.end_date
    }

    setManualReplaceLoading(true)
    try {
      const response = await fetchData(
        'POST',
        '/v3/reports/icd-inactive-report/bulk-replace',
        payload
      )
      if (response) {
        toast.success(
          `${t('Replacement completed successfully')}! ${
            response.total_emrs_updated
          } ${t('EMRs updated')}`
        )
        onSuccess?.(response)
        setReplacements([{ old_code: '', new_code: '' }])
      }
    } catch (error) {
      if (error?.response?.data?.codes_not_found) {
        toast.error(
          `${t('Some new codes were not found in the ICD collection')}: ${error.response.data.codes_not_found.join(
            ', '
          )}`
        )
      } else if (error?.response?.data?.error) {
        toast.error(error.response.data.error)
      } else {
        toast.error(t('Failed to replace ICD codes'))
      }
    } finally {
      setManualReplaceLoading(false)
    }
  }

  const handleDownload = async () => {
    const downloadUrl =
      jobStatus?.download_url ||
      (jobId ? `/v3/invoices/audit/icd-audit/job/${jobId}/download` : '')

    if (!downloadUrl) {
      toast.error(t('Download URL is not available yet'))
      return
    }

    setDownloading(true)
    await downloadFile(
      'GET',
      downloadUrl.replace('/api', ''),
      {},
      jobStatus?.file_name || 'icd_audit.xlsx'
    )
    setHasAutoDownloaded(true)
    setDownloading(false)
  }

  const handleRefreshStatus = async () => {
    if (!jobId) return
    await fetchAuditJobStatus(jobId)
  }

  const handleRefreshUploadStatus = async () => {
    if (!uploadJobId) return
    await fetchUploadJobStatus(uploadJobId)
  }

  const resetState = () => {
    setActiveTabIndex(0)
    setLoading(false)
    setStatusLoading(false)
    setUploadLoading(false)
    setUploadStatusLoading(false)
    setStopResumeLoading(false)
    setDownloading(false)
    setJobId('')
    setJobStatus(null)
    setShouldPollAuditStatus(false)
    setUploadJobId('')
    setUploadJobStatus(null)
    setShouldPollUploadStatus(false)
    setFile(null)
    setHasAutoDownloaded(false)
    setAuditCompletionHandled(false)
    setUploadCompletionHandled(false)
    setManualReplaceLoading(false)
    setIcdSuggestions([])
    setSearchingIcd(false)
    setReplacements([{ old_code: '', new_code: '' }])
    setFormData(initialDateState())
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const renderAuditTab = () => (
    <div className='flex flex-col gap-4 pt-1'>
      <div className='grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-4 md:grid-cols-2'>
        <DatePicker
          label={t('Start Date')}
          name='start_date'
          value={
            formData.start_date
              ? moment(formData.start_date, 'YYYY-MM-DD').toDate()
              : ''
          }
          onChange={handleDateChange}
          useFormik={false}
          placeholder={t('Select Start Date')}
          disabled={loading || !!jobId}
        />
        <DatePicker
          label={t('End Date')}
          name='end_date'
          value={
            formData.end_date
              ? moment(formData.end_date, 'YYYY-MM-DD').toDate()
              : ''
          }
          onChange={handleDateChange}
          useFormik={false}
          placeholder={t('Select End Date')}
          disabled={loading || !!jobId}
        />
      </div>

      {jobStatus && (
        <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
          <div className='flex flex-wrap items-start justify-between gap-3'>
            <div className='space-y-1'>
              <h3 className='text-xl font-semibold text-gray-800'>
                {t('Batch Status')}
              </h3>
              <div>
                <div className='text-xs text-gray-500'>{t('Job ID')}</div>
                <div className='font-mono text-sm text-gray-800 break-all'>
                  {jobStatus.job_id || jobId}
                </div>
              </div>
            </div>
            <span
              className={`inline-flex items-center rounded-full px-4 py-1 text-sm font-semibold capitalize ${statusColors.pill}`}
            >
              {t(jobStatus.status || 'queued')}
            </span>
          </div>

          <div className='mt-4'>
            <div className='mb-2 flex items-center justify-between gap-3 text-sm'>
              <span className='font-medium text-gray-700'>{t('Progress')}</span>
              <span className='font-medium text-gray-600'>
                {progressValue.toFixed(1)}%
              </span>
            </div>
            <div className='h-5 overflow-hidden rounded-md bg-[#d9e2ff]'>
              <div
                className={`h-full rounded-md transition-all duration-500 ${statusColors.bar}`}
                style={{ width: `${Math.max(progressValue, 0)}%` }}
              />
            </div>
          </div>

          <div className='mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4'>
            {summaryCards.map(card => (
              <div key={card.key} className={`rounded-lg p-3 ${card.className}`}>
                <div className='text-sm text-gray-500'>{card.label}</div>
                <div className='text-3xl font-semibold leading-tight'>
                  {card.value}
                </div>
              </div>
            ))}
          </div>

          <div className='mt-4 grid grid-cols-1 gap-3 text-sm md:grid-cols-2'>
            <div>
              <span className='text-gray-600'>{t('Audit Type')}:</span>
              <span className='ml-2 font-medium text-gray-800'>
                {'audit_by_date_range'}
              </span>
            </div>
            <div>
              <span className='text-gray-600'>{t('Created At')}:</span>
              <span className='ml-2 font-medium text-gray-800'>
                {jobStatus.created_at || jobStatus.completed_at || '-'}
              </span>
            </div>
            <div>
              <span className='text-gray-600'>{t('Date Range')}:</span>
              <span className='ml-2 font-medium text-gray-800'>
                {jobStatus.start_date && jobStatus.end_date
                  ? `${jobStatus.start_date} to ${jobStatus.end_date}`
                  : t('All Records')}
              </span>
            </div>
            <div>
              <span className='text-gray-600'>{t('Auto Download')}:</span>
              <span className='ml-2 font-medium text-gray-800'>
                {hasAutoDownloaded ? t('Completed') : t('Pending')}
              </span>
            </div>
          </div>

          {jobStatus?.error && (
            <div className='mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700'>
              {jobStatus.error}
            </div>
          )}

          <div className='mt-4 flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-4'>
            <CustomButton
              label={t('Stop Job')}
              buttonType='danger'
              icon={FaPause}
              onClick={handleStopAuditJob}
              disabled={
                stopResumeLoading ||
                !['queued', 'running', 'processing'].includes(auditStatus)
              }
              loading={
                stopResumeLoading &&
                ['queued', 'running', 'processing'].includes(auditStatus)
              }
              className='w-fit'
            />
            <CustomButton
              label={t('Resume Job')}
              buttonType='blue'
              icon={FaPlay}
              onClick={handleResumeAuditJob}
              disabled={stopResumeLoading || !canResumeAuditStatus(auditStatus)}
              loading={stopResumeLoading && canResumeAuditStatus(auditStatus)}
              className='w-fit'
            />
            <CustomButton
              label={t('Refresh Status')}
              buttonType='reset'
              icon='pi pi-refresh'
              onClick={handleRefreshStatus}
              disabled={statusLoading || downloading || stopResumeLoading}
              loading={statusLoading}
              className='w-fit'
            />
            <CustomButton
              label={t('Download Excel')}
              buttonType='success'
              icon={FaFileExcel}
              onClick={handleDownload}
              disabled={
                downloading ||
                auditStatus !== 'completed' ||
                !jobStatus?.download_url
              }
              loading={downloading}
              className='w-fit'
            />
          </div>
        </div>
      )}
    </div>
  )

  const renderUploadTab = () => (
    <div className='flex flex-col gap-4 pt-1'>
      <div className='grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-4 md:grid-cols-2'>
        <DatePicker
          label={t('From Date')}
          name='start_date'
          value={
            formData.start_date
              ? moment(formData.start_date, 'YYYY-MM-DD').toDate()
              : ''
          }
          onChange={handleDateChange}
          useFormik={false}
          placeholder={t('Select Start Date')}
          disabled={uploadLoading || !!uploadJobId}
        />
        <DatePicker
          label={t('To Date')}
          name='end_date'
          value={
            formData.end_date
              ? moment(formData.end_date, 'YYYY-MM-DD').toDate()
              : ''
          }
          onChange={handleDateChange}
          useFormik={false}
          placeholder={t('Select End Date')}
          disabled={uploadLoading || !!uploadJobId}
        />
      </div>

      <div className='rounded-xl border border-gray-200 bg-white p-4'>
        <UploadExcelFile
          isLoading={uploadLoading || !!uploadJobId}
          file={file}
          setFile={setFile}
        />
      </div>

      {uploadJobStatus && (
        <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
          <div className='flex flex-wrap items-start justify-between gap-3'>
            <div className='space-y-1'>
              <h3 className='text-xl font-semibold text-gray-800'>
                {t('Upload Job Status')}
              </h3>
              <div>
                <div className='text-xs text-gray-500'>{t('Job ID')}</div>
                <div className='font-mono text-sm text-gray-800 break-all'>
                  {uploadJobStatus.job_id || uploadJobId}
                </div>
              </div>
            </div>
            <span
              className={`inline-flex items-center rounded-full px-4 py-1 text-sm font-semibold capitalize ${uploadStatusColors.pill}`}
            >
              {t(uploadJobStatus.status || 'queued')}
            </span>
          </div>

          <div className='mt-4'>
            <div className='mb-2 flex items-center justify-between gap-3 text-sm'>
              <span className='font-medium text-gray-700'>{t('Progress')}</span>
              <span className='font-medium text-gray-600'>
                {uploadProgressValue.toFixed(1)}%
              </span>
            </div>
            <div className='h-5 overflow-hidden rounded-md bg-[#d9e2ff]'>
              <div
                className={`h-full rounded-md transition-all duration-500 ${uploadStatusColors.bar}`}
                style={{ width: `${Math.max(uploadProgressValue, 0)}%` }}
              />
            </div>
          </div>

          <div className='mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4'>
            {uploadCards.map(card => (
              <div key={card.key} className={`rounded-lg p-3 ${card.className}`}>
                <div className='text-sm text-gray-500'>{card.label}</div>
                <div className='text-3xl font-semibold leading-tight'>
                  {card.value}
                </div>
              </div>
            ))}
          </div>

          <div className='mt-4 grid grid-cols-1 gap-3 text-sm md:grid-cols-2'>
            <div>
              <span className='text-gray-600'>{t('From Date')}:</span>
              <span className='ml-2 font-medium text-gray-800'>
                {uploadJobStatus.from_date || '-'}
              </span>
            </div>
            <div>
              <span className='text-gray-600'>{t('To Date')}:</span>
              <span className='ml-2 font-medium text-gray-800'>
                {uploadJobStatus.to_date || '-'}
              </span>
            </div>
            <div>
              <span className='text-gray-600'>{t('Created At')}:</span>
              <span className='ml-2 font-medium text-gray-800'>
                {uploadJobStatus.created_at || uploadJobStatus.completed_at || '-'}
              </span>
            </div>
            <div>
              <span className='text-gray-600'>{t('Status')}:</span>
              <span className='ml-2 font-medium text-gray-800'>
                {uploadJobStatus.status || '-'}
              </span>
            </div>
          </div>

          {uploadJobStatus?.error && (
            <div className='mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700'>
              {uploadJobStatus.error}
            </div>
          )}

          <div className='mt-4 flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-4'>
            <CustomButton
              label={t('Stop Job')}
              buttonType='danger'
              icon={FaPause}
              onClick={handleStopUploadJob}
              disabled={
                stopResumeLoading ||
                !['queued', 'running', 'processing'].includes(uploadStatus)
              }
              loading={
                stopResumeLoading &&
                ['queued', 'running', 'processing'].includes(uploadStatus)
              }
              className='w-fit'
            />
            <CustomButton
              label={t('Resume Job')}
              buttonType='blue'
              icon={FaPlay}
              onClick={handleResumeUploadJob}
              disabled={stopResumeLoading || !canResumeUploadStatus(uploadStatus)}
              loading={stopResumeLoading && canResumeUploadStatus(uploadStatus)}
              className='w-fit'
            />
            <CustomButton
              label={t('Refresh Status')}
              buttonType='reset'
              icon='pi pi-refresh'
              onClick={handleRefreshUploadStatus}
              disabled={uploadStatusLoading || stopResumeLoading}
              loading={uploadStatusLoading}
              className='w-fit'
            />
          </div>
        </div>
      )}
    </div>
  )

  const manualColumns = [
    {
      field: 'index',
      header: t('#'),
      body: (rowData, { rowIndex }) => (
        <span className='text-sm text-gray-600'>{rowIndex + 1}</span>
      )
    },
    {
      field: 'old_code',
      header: t('Old Code (Inactive)'),
      body: (rowData, { rowIndex }) => (
        <TextInput
          name={`old_code_${rowIndex}`}
          value={rowData.old_code}
          handleChange={e =>
            updateReplacement(rowIndex, 'old_code', e.target.value)
          }
          placeholder={t('e.g., S09.8')}
          useFormik={false}
          disabled={manualReplaceLoading}
          parentClass='mb-0'
        />
      )
    },
    {
      field: 'new_code',
      header: t('New Code (Active)'),
      body: (rowData, { rowIndex }) => (
        <AutoComplete
          value={rowData.new_code}
          suggestions={icdSuggestions}
          completeMethod={handleSearchIcd}
          field='label'
          onChange={e => updateReplacement(rowIndex, 'new_code', e.value)}
          onSelect={e => handleSelectIcd(e, rowIndex)}
          placeholder={t('Search ICD code...')}
          disabled={manualReplaceLoading}
          delay={300}
          minLength={3}
          forceSelection={false}
          emptyMessage={t('No ICD codes found')}
          showEmptyMessage
          loadingIcon={
            searchingIcd ? (
              <i
                className={`pi pi-spin pi-spinner text-primary absolute top-[9px] ${
                  i18n.language === 'en' ? 'right-[12px]' : 'left-[12px]'
                }`}
              />
            ) : (
              <span />
            )
          }
          pt={{
            root: { className: '!w-full' },
            input: { root: { className: 'input-field !opacity-100 !w-full' } },
            panel: {
              className: '!shadow-lg rounded-[8px] mt-2 pb-2 overflow-hidden'
            },
            item: { className: 'hover:text-primary capitalize' },
            loadingIcon: {
              className: `absolute top-[13px] ${
                i18n.language === 'en' ? 'right-[12px]' : 'left-[12px]'
              }`
            },
            emptymesage: { className: '!text-center' }
          }}
        />
      )
    },
    {
      field: 'actions',
      header: t('Actions'),
      headerCellClass: 'flex items-center justify-center',
      body: (rowData, { rowIndex }) => (
        <div className='flex items-center justify-center'>
          <DeleteIcon
            iconSize='20'
            onClick={() => removeReplacement(rowIndex)}
            disabled={replacements.length === 1 || manualReplaceLoading}
            title={t('Remove')}
          />
        </div>
      )
    }
  ]

  const renderManualReplaceTab = () => (
    <div className='flex flex-col gap-4 pt-1'>
      <div className='grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-4 md:grid-cols-2'>
        <DatePicker
          label={t('Start Date')}
          name='start_date'
          value={
            formData.start_date
              ? moment(formData.start_date, 'YYYY-MM-DD').toDate()
              : ''
          }
          onChange={handleDateChange}
          useFormik={false}
          placeholder={t('Select Start Date')}
          disabled={manualReplaceLoading}
        />
        <DatePicker
          label={t('End Date')}
          name='end_date'
          value={
            formData.end_date
              ? moment(formData.end_date, 'YYYY-MM-DD').toDate()
              : ''
          }
          onChange={handleDateChange}
          useFormik={false}
          placeholder={t('Select End Date')}
          disabled={manualReplaceLoading}
        />
      </div>

      <CustomTable
        data={replacements}
        columns={manualColumns}
        isLoading={false}
        emptyMessage={t('No replacements added yet')}
      />

      <div className='flex items-center justify-end'>
        <button
          className='flex items-center gap-1 border-0 text-blue underline'
          onClick={addReplacement}
          disabled={replacements.length >= 500 || manualReplaceLoading}
        >
          <FaPlus /> {t('Add New Row')}
        </button>
      </div>
    </div>
  )

  const tabs = [
    {
      name: t('Audit ICD'),
      content: renderAuditTab
    },
    {
      name: t('Update ICD by Excel'),
      content: renderUploadTab
    }
  ]

  return (
    <ReusableModal
      isVisible={isVisible}
      title={t('Audit ICD')}
      HeaderIcon={FaSearch}
      onClose={handleClose}
      className='w-full lg:w-[1080px]'
      position='top'
    >
      <div className='flex flex-col gap-4'>
        <CustomTabs
          tabs={tabs}
          activeTabIndex={activeTabIndex}
          setActiveTabIndex={setActiveTabIndex}
          className='w-full'
          tabClassName='!bg-white'
          panelClassName='!bg-transparent'
        />

        <div className='flex justify-end gap-2 border-t pt-2'>
          <CustomButton
            label={t('Close')}
            buttonType='reset'
            onClick={handleClose}
            disabled={
              loading ||
              statusLoading ||
              uploadLoading ||
              uploadStatusLoading ||
              stopResumeLoading ||
              downloading ||
              manualReplaceLoading
            }
            className='w-fit'
            icon='pi pi-times'
          />
          {activeTabIndex === 0 && !jobId && (
            <CustomButton
              label={t('Start ICD Job')}
              buttonType='blue'
              icon='pi pi-play'
              onClick={handleSubmitAudit}
              disabled={loading}
              loading={loading}
              className='w-fit'
            />
          )}
          {activeTabIndex === 1 && (
            <CustomButton
              label={t('Upload ICD Excel')}
              buttonType='blue'
              icon={FaUpload}
              onClick={handleUpload}
              disabled={uploadLoading || !!uploadJobId || !file}
              loading={uploadLoading}
              className='w-fit'
            />
          )}
          {activeTabIndex === 2 && (
            <CustomButton
              label={t('Replace All')}
              buttonType='primary'
              icon={FaExchangeAlt}
              onClick={handleManualReplace}
              disabled={
                manualReplaceLoading ||
                replacements.length === 0 ||
                replacements.some(r => !r.old_code || !r.new_code)
              }
              loading={manualReplaceLoading}
              className='w-fit'
            />
          )}
        </div>
      </div>
    </ReusableModal>
  )
}

export default ICDBulkReplaceModal
